import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  Divider,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import { useSelector } from "react-redux";
import { clientesApi } from "src/services/clientesApi";
import { makeStyles } from "@material-ui/styles";
import { useSnackbar } from "src/components/snackbar";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 500,
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  clienteInfo: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  dateField: {
    "& input": {
      fontSize: "0.9rem",
    },
  },
  descripcionField: {
    "& .MuiOutlinedInput-root": {
      alignItems: "flex-start",
    },
  },
  actions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
  errorAlert: {
    marginBottom: theme.spacing(2),
  },
}));

export default function AgregarPortafolio({
  open,
  onClose,
  cliente,
  portafolio = null,
}) {
  const classes = useStyles();
  const { sucursal } = useSelector((state) => state.auth.user);

  // Estado del formulario
  const [formData, setFormData] = useState({
    descripcion: "",
  });
  const [error, setError] = useState(null);

  const [agregarPortafolio, { isLoading: loadingAgregar }] =
    clientesApi.useAgregarPortafolioClienteMutation();
  const [actualizarPortafolio, { isLoading: loadingActualizar }] =
    clientesApi.useActualizarPortafolioClienteMutation();
  const { enqueueSnackbar } = useSnackbar();

  const isEditing = Boolean(portafolio);
  const isLoading = loadingAgregar || loadingActualizar;

  // Cargar datos del portafolio si estamos editando
  useEffect(() => {
    if (isEditing && portafolio) {
      setFormData({
        descripcion: portafolio.descripcion || "",
      });
    } else {
      setFormData({
        descripcion: "",
      });
    }
    setError(null);
  }, [isEditing, portafolio, open]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Limpiar error al editar
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!formData.descripcion.trim()) {
      setError("La descripción es requerida");
      return false;
    }

    if (formData.descripcion.trim().length < 10) {
      setError("La descripción debe tener al menos 10 caracteres");
      return false;
    }

    if (!cliente?.rut) {
      setError("Cliente no válido");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isEditing) {
        await actualizarPortafolio({
          id: portafolio.id,
          descripcion: formData.descripcion,
          refSucursal: sucursal?.refSucursal,
        }).unwrap();
        enqueueSnackbar("¡Portafolio editado correctamente!", {
          variant: "success",
        });
      } else {
        await agregarPortafolio({
          refCliente: cliente.rut,
          descripcion: formData.descripcion,
        }).unwrap();
        enqueueSnackbar("¡Portafolio creado correctamente!", {
          variant: "success",
        });
      }

      onClose();
    } catch (err) {
      console.error("Error al guardar portafolio:", err);
      setError(
        err.data?.message ||
          `Error al ${
            isEditing ? "actualizar" : "crear"
          } el portafolio. Intenta nuevamente.`,
      );
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className={classes.header}>
          <Typography variant="h6">
            {isEditing ? "Editar Portafolio" : "Nuevo Portafolio"}
          </Typography>
        </div>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Información del cliente */}
        <Box className={classes.clienteInfo}>
          <Typography variant="subtitle2" color="textSecondary">
            Cliente
          </Typography>
          <Typography variant="body1" style={{ fontWeight: 500 }}>
            {cliente?.nombre || "Cliente no especificado"}
          </Typography>
          {cliente?.rut && (
            <Typography variant="body2" color="textSecondary">
              RUT: {cliente.rut}
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" className={classes.errorAlert}>
            {error}
          </Alert>
        )}

        <div className={classes.form}>
          <TextField
            label="Descripción de Actividades"
            multiline
            rows={6}
            value={formData.descripcion}
            onChange={handleInputChange("descripcion")}
            placeholder="Describe detalladamente las actividades realizadas, acuerdos tomados, próximos pasos..."
            className={`${classes.textField} ${classes.descripcionField}`}
            disabled={isLoading}
            fullWidth
            required
            variant="outlined"
            helperText={`${formData.descripcion.length}/1000 caracteres`}
            inputProps={{ maxLength: 1000 }}
          />
        </div>
      </DialogContent>

      <Divider />

      <DialogActions className={classes.actions}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancelar
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={isLoading}
          variant="contained"
          color="primary"
          disabled={!formData.descripcion.trim()}
        >
          {isEditing ? "Actualizar" : "Crear"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
